import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { AxiosCacheInstance, setupCache } from 'axios-cache-interceptor';
import { store } from 'store/index';
import { getRecaptchaTokenForUrl, RECAPTCHA_HEADER } from './Recaptcha';

/**
 * Axios request interceptor that attaches a fresh reCAPTCHA v3 token to
 * open-proxy calls. Registered on both {@link apiInstance} and
 * {@link apiInstanceCached} so every outbound API call gets uniform
 * treatment without touching individual call sites.
 *
 * The header is only added when {@link getRecaptchaTokenForUrl} returns a
 * token — i.e. the URL matches the configured open-proxy base and
 * reCAPTCHA has been initialised. All other requests pass through
 * untouched.
 */
const attachRecaptchaToken = async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
	const token = await getRecaptchaTokenForUrl(config.url);
	if (token) {
		config.headers.set(RECAPTCHA_HEADER, token);
	}
	return config;
};

/**
 * If the backend rejects a request with a 400 that mentions reCAPTCHA in the
 * error detail, surface a single high-signal log line. This distinguishes
 * “we forgot to attach the token” from “backend rejected the token” — a
 * failure mode that isn’t visible from anywhere else in the app.
 */
const logRecaptchaRejection = (error: unknown, source: string): void => {
	const err = error as {
		response?: { status?: number; data?: { detail?: unknown; title?: unknown } };
		config?: { url?: string; headers?: Record<string, unknown> & { get?: (name: string) => unknown } };
	};
	const status = err?.response?.status;
	const detail = err?.response?.data?.detail ?? err?.response?.data?.title;
	if (status !== 400 || typeof detail !== 'string' || !/recaptcha/i.test(detail)) {
		return;
	}
	const headers = err?.config?.headers;
	const sent = !!headers?.[RECAPTCHA_HEADER] || !!headers?.get?.(RECAPTCHA_HEADER);
	const url = err?.config?.url;
	console.error(
		`[recaptcha] backend rejected ${source} request to ${url}: "${detail}" (${RECAPTCHA_HEADER} sent=${sent})`
	);
};

/**
 * Create a fresh axios instance for a single logical caller.
 *
 * Interceptors:
 * - Request: attaches an `X-Recaptcha-Token` header on open-proxy calls.
 * - Response: unwraps `response.data`; logs and re-throws on error.
 *
 * @param timeout Request timeout in milliseconds (default: 100 000).
 */
export const apiInstance = (timeout = 100000): AxiosInstance => {
	const axiosInstance = axios.create({
		timeout,
	});

	// add X-Site-Name header to requests
	// const theme = 'CODAN';
	// axios.defaults.headers.common['X-Site-Name'] = theme;

	axiosInstance.interceptors.request.use(attachRecaptchaToken);

	// Add the error handling interceptor
	axiosInstance.interceptors.response.use(
		(response) => response.data,
		(error) => {
			logRecaptchaRejection(error, 'axios');
			console.error('API error', error);
			throw error;
		}
	);

	return axiosInstance;
};

let axiosCacheInstance: AxiosCacheInstance;
/**
 * Singleton axios instance wrapped by `axios-cache-interceptor`.
 *
 * Caches GET and POST responses for 10 minutes to keep expensive lookups
 * (SPD search, calculation with identical inputs, list-of-values, …)
 * responsive. The same reCAPTCHA interceptor as {@link apiInstance} is
 * attached — cached responses are returned with `axiosCached: true`
 * regardless of whether a fresh token was generated.
 */
export const apiInstanceCached = (): AxiosCacheInstance => {
	if (axiosCacheInstance) {
		return axiosCacheInstance;
	}

	const axiosInstance = axios.create({
		timeout: 65000,
	});

	axiosCacheInstance = setupCache(axiosInstance, {
		ttl: 1000 * 60 * 10, // 10 minutes cache
		methods: ['get', 'post'],
		interpretHeader: false, // override no-cache
	});

	axiosInstance.interceptors.request.use(attachRecaptchaToken);

	axiosInstance.interceptors.response.use(
		(response) => ({ ...response.data, axiosCached: (response as any).cached }),
		(error) => {
			logRecaptchaRejection(error, 'axios-cached');
			console.error('API error', error);
			throw error;
		}
	);

	return axiosCacheInstance;
};

export const getBaseProxyUrl = (metaAreaContent: MetaAreaContent, url: string) => {
	if (!url || !metaAreaContent) {
		return;
	}
	const isLoggedIn = store.getState().customer.isLoggedIn;

	let baseUrl = metaAreaContent.content.baseUrlOpenProxy.url;

	if (isLoggedIn) {
		baseUrl = metaAreaContent.content.baseUrlAuthenticatedProxy.url;
	}
	console.log('baseUrl', baseUrl);

	return baseUrl + url;
};
